import FormControl from "@ui-kit/form/FormControl/FormControl.web";
import Input, { InputProps } from "@ui-kit/form/Input/Input.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import { useTranslation } from "@shared/utils/internationalization";
import { Controller, FieldError, useFormContext } from "react-hook-form";
import { DetailedInputSharedProps } from "./DetailedInput.props";

/**
 * Input component with a label, helper text and error message.
 * This should be used alongside a parent provider in the render tree. For standalone inputs,
 * @see {@link Input}
 */
export default function DetailedInput(props: DetailedInputProps) {
  const { t } = useTranslation("common");
  const { name, label, helperText, required, inputProps } = props;
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const hasError = typeof errors[name]?.message === "string";

  return (
    <Flex direction="column" gap="2xs">
      <Box>
        <Text
          fontWeight={600}
          fontSize="sm"
          color="font.dark"
          as="span"
          data-testid={`input-${name}-label`}
        >
          {label}
        </Text>
        {!required && (
          <Text color="gray.600" as="span">
            {" "}
            ({t("Optional")})
          </Text>
        )}
      </Box>
      <FormControl isInvalid={hasError}>
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              {...inputProps}
              id={name}
              data-testid={`input-${name}`}
              errorBorderColor="red.600"
            />
          )}
        />
      </FormControl>
      {helperText && !hasError && (
        <Text
          fontSize="xs"
          color="gray.600"
          data-testid={`input-${name}-helper`}
        >
          {helperText}
        </Text>
      )}
      {hasError && (
        <Text fontSize="xs" color="red.600" data-testid={`input-${name}-error`}>
          {(errors[name] as FieldError)?.message}
        </Text>
      )}
    </Flex>
  );
}

type DetailedInputProps = DetailedInputSharedProps & {
  /**
   * On web, this is used for UI purposes, Not validation.
   * Validation should continue to be based on the schema instead of props.
   */
  required?: boolean;
  /**
   * Props that target the internal Input component being used
   */
  inputProps?: InputProps;
};
